import type { DemandDto } from '../api/models/DemandDto';

/** The slice of DemandControllerService that the wizard's step 1 needs. */
export interface DemandPersistenceApi {
  create(requestBody: DemandDto): PromiseLike<DemandDto>;
  update(uuid: string, requestBody: DemandDto): PromiseLike<DemandDto>;
  delete(uuid: string): PromiseLike<unknown>;
}

/**
 * Writes the pending step-1 demand edits: deletions first, then each edited cell.
 *
 * A cell that edits an existing demand carries that demand's uuid and is sent as an update.
 * Creating it anew instead produced a second row: the backend upserts by
 * material + date + due time, the grid edit has no due time, so the new row got the default
 * time (06:00 or the first delivery time), while the imported row with its own due time stayed.
 * The production quantities then summed both rows (review round 5, finding 1.1). The update
 * keeps the existing due time, because the backend leaves it untouched when the payload has none.
 *
 * An update supersedes a deletion of the same uuid. The user can clear a cell and then type a
 * value into it again before saving; the clear leaves the uuid in the pending deletions while the
 * re-edit adds it to the saves. Deleting first and updating afterwards made the backend answer
 * "Demand not found" and the whole save failed, leaving the user stuck on step 1. Dropping such
 * deletions here keeps the invariant with the single writer instead of with each caller.
 */
export async function persistStep1Demands(
  toDelete: readonly string[],
  toSave: readonly DemandDto[],
  api: DemandPersistenceApi,
): Promise<void> {
  const savedUuids = savedUuidsOf(toSave);
  for (const uuid of toDelete) {
    if (savedUuids.has(uuid)) {
      continue;
    }
    await api.delete(uuid);
  }
  for (const demand of toSave) {
    const body: DemandDto = { ...demand, uuid: undefined, demandType: demand.demandType || 'ORDER' };
    if (demand.uuid) {
      await api.update(demand.uuid, body);
    } else {
      await api.create(body);
    }
  }
}

/** The uuids of the demands that are updated rather than created. */
export function savedUuidsOf(toSave: readonly DemandDto[]): Set<string> {
  const uuids = new Set<string>();
  for (const demand of toSave) {
    if (demand.uuid) {
      uuids.add(demand.uuid);
    }
  }
  return uuids;
}
